import { Box, Icon, Typography } from "@outschool/backpack";
import { UpdateWaitlistMutation } from "@outschool/gql-frontend-generated";
import { faCheckCircle } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { TrackedButton } from "@outschool/ui-components-shared";
import { cleanErrorMessage } from "@outschool/ui-utils";
import React from "react";

import { useUpdateWaitlist } from "../../queries/WaitlistSeatMutations";
import ActivitySummary from "../enrolling/ActivitySummary";
import LoadingWithMessage from "../LoadingWithMessage";
import LeaveWaitlistForm, { LeaveWaitlistFormProps } from "./LeaveWaitlistForm";

enum Step {
  WaitlistForm = "waitlistForm",
  UpdatingWaitlist = "updatingWaitlist",
  WaitlistUpdated = "waitlistUpdated",
  Errored = "errored"
}

interface State {
  step: Step;
  error: string | null | undefined;
}

enum ActionType {
  UpdatingWaitlist = "UPDATING_WAITLIST",
  WaitlistUpdated = "WAITLIST_UPDATED",
  Errored = "ERRORED"
}

type Action =
  | { type: ActionType.UpdatingWaitlist }
  | { type: ActionType.Errored; error?: string }
  | {
      type: ActionType.WaitlistUpdated;
      value: {
        deletedSeats: UpdateWaitlistMutation["deleteWaitlistSeats"];
      };
    };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionType.UpdatingWaitlist:
      return { ...state, step: Step.UpdatingWaitlist };
    case ActionType.WaitlistUpdated: {
      return {
        ...state,
        ...action.value,
        step: Step.WaitlistUpdated
      };
    }
    case ActionType.Errored:
      return { ...state, step: Step.Errored, error: action.error };
    default:
      return state;
  }
}

const initialState = {
  error: null,
  step: Step.WaitlistForm,
  waitlistSeats: [],
  selectedLearnerForMessage: null
};

export type LeaveWaitlistModalProps = Omit<
  LeaveWaitlistFormProps,
  "error" | "handleUpdateWaitlist"
> & {
  close: () => void;
};

export default function LeaveWaitlistModal({
  key,
  learner,
  activity,
  waitlistSeats,
  close
}: LeaveWaitlistModalProps) {
  const { t } = useTranslation(
    "client\\components\\enrolling\\ActivityManageWaitlist"
  );

  const [state, dispatch] = React.useReducer(reducer, initialState);

  const [updateWaitlist] = useUpdateWaitlist();

  const handleUpdateWaitlist = React.useCallback(
    async (seatsToRemove: $TSFixMe) => {
      dispatch({ type: ActionType.UpdatingWaitlist });
      try {
        const { data } = await updateWaitlist({
          variables: {
            createInputs: [],
            deleteInputs: seatsToRemove
          }
        });

        const { deleteWaitlistSeats: deletedSeats } = data!;

        dispatch({
          type: ActionType.WaitlistUpdated,
          value: { deletedSeats }
        });
      } catch (error) {
        dispatch({
          type: ActionType.Errored,
          error: cleanErrorMessage(error.message)
        });
      }
    },
    [updateWaitlist, dispatch]
  );

  /**
   * TODO(waitlist): modal represents multiple sections, so we should find
   * another way to represent the ActivitySummary, like in the Figma
   */
  const section = waitlistSeats[0].section;

  return (
    <>
      <Typography
        variant="h5"
        component="h2"
        gutterBottom
        sx={{
          marginBottom: "2rem"
        }}
      >
        {state.step === Step.WaitlistUpdated ? (
          <>
            <Icon sx={{ marginRight: 2 }} icon={faCheckCircle} />{" "}
            {t("Your waitlist settings have been updated!")}
          </>
        ) : (
          t("Manage Waitlist")
        )}
      </Typography>

      {/* Activity info */}
      <ActivitySummary
        activity={activity}
        startTime={section.start_time}
        endTime={section.end_time}
      />

      {/* Form view */}
      {state.step === Step.WaitlistForm && (
        <LeaveWaitlistForm
          key={key}
          learner={learner}
          activity={activity}
          waitlistSeats={waitlistSeats}
          error={state.error!}
          handleUpdateWaitlist={handleUpdateWaitlist}
        />
      )}

      {/* Loading view */}
      {state.step === Step.UpdatingWaitlist && (
        <LoadingWithMessage>
          {t`Please wait while we process your waitlist request. Please DO \
NOT close this screen or hit the back button.`}
        </LoadingWithMessage>
      )}

      {/* Error view */}
      {state.step === Step.Errored && (
        <Box>
          <div>{state.error}</div>
        </Box>
      )}

      {/* Operation complete view */}
      {state.step === Step.WaitlistUpdated && (
        <Box>
          <TrackedButton
            onClick={close}
            variant="contained"
            trackingName={"waitlisting_complete_modal_close"}
            trackingUniqueId={key}
          >
            {t("Done")}
          </TrackedButton>
        </Box>
      )}
    </>
  );
}
